import { useRef } from 'react';

import type { ApolloCache, Reference } from '@apollo/client';
import { Apollo } from 'src/gql/generated';
import type { BulkCreateDocumentRelationshipsMutation } from 'src/gql/generated/apollo-types';

type CreatedRelationship =
  BulkCreateDocumentRelationshipsMutation['bulkCreateDocumentRelationships'][number];

function mergeCreatedRelationshipsIntoDocumentCache(
  cache: ApolloCache,
  sourceDocumentId: string,
  relationships: CreatedRelationship[]
) {
  const parentRefs: Reference[] = [];
  const childRefs: Reference[] = [];

  for (const rel of relationships) {
    const relRef = cache.identify({ __typename: 'DocumentRelationship', id: rel.id });
    if (!relRef) continue;

    const ref: Reference = { __ref: relRef };
    /**
     * A self-referencing edge has both IDs equal to the source document, and the
     * server lists it only under childRelationships. Check the parent side first
     * so those edges land there instead of under parentRelationships.
     */
    if (rel.parentDocumentId === sourceDocumentId) childRefs.push(ref);
    else if (rel.childDocumentId === sourceDocumentId) parentRefs.push(ref);
  }

  if (parentRefs.length === 0 && childRefs.length === 0) return;

  cache.modify({
    fields: {
      childRelationships: (existing: readonly Reference[] = []) =>
        appendUniqueRefs(existing, childRefs),
      parentRelationships: (existing: readonly Reference[] = []) =>
        appendUniqueRefs(existing, parentRefs),
    },
    id: cache.identify({ __typename: 'Document', id: sourceDocumentId }),
  });
}

function appendUniqueRefs(
  existing: readonly Reference[],
  incoming: Reference[]
): readonly Reference[] {
  if (incoming.length === 0) return existing;
  const seen = new Set(existing.map(r => r.__ref));
  const extra = incoming.filter(r => !seen.has(r.__ref));
  return extra.length === 0 ? existing : [...existing, ...extra];
}

/**
 * Bulk-create document relationships and append returned relationships onto `documentId`'s
 * parent/child relationship lists in the Apollo cache (via mutation `update`).
 */
export function useBulkCreateRelationships(documentId: string) {
  const documentIdRef = useRef(documentId);
  documentIdRef.current = documentId;

  return Apollo.useBulkCreateDocumentRelationshipsMutation({
    update(cache, { data }) {
      const created = data?.bulkCreateDocumentRelationships;
      if (!created?.length) return;
      mergeCreatedRelationshipsIntoDocumentCache(cache, documentIdRef.current, created);
    },
  });
}
